/**
 * Cash-Out Earning affiliate Points — eligibility & cash-out % of Points Earnings.
 * Level 1: total Ultra members in tree.
 * Levels 2–10: team size with max 50% of required minimum per level-1 leg.
 */

export const CASH_OUT_AFFILIATE_TEAM_LEG_CAP_FRACTION = 0.5;

export const CASH_OUT_AFFILIATE_LEVELS = [
  { level: 1, ultraRequired: 10, directReferrals: 10, cashOutPercent: 50 },
  { level: 2, ultraRequired: 50, directReferrals: 10, cashOutPercent: 60 },
  { level: 3, ultraRequired: 250, directReferrals: 15, cashOutPercent: 65 },
  { level: 4, ultraRequired: 1000, directReferrals: 20, cashOutPercent: 70 },
  { level: 5, ultraRequired: 3000, directReferrals: 25, cashOutPercent: 75 },
  { level: 6, ultraRequired: 6000, directReferrals: 30, cashOutPercent: 80 },
  { level: 7, ultraRequired: 12000, directReferrals: 35, cashOutPercent: 85 },
  { level: 8, ultraRequired: 25000, directReferrals: 40, cashOutPercent: 90 },
  { level: 9, ultraRequired: 50000, directReferrals: 45, cashOutPercent: 95 },
  { level: 10, ultraRequired: 100000, directReferrals: 50, cashOutPercent: 100 },
];

/** @deprecated Use cashOutPercent — kept for existing CES / cesCoins field names */
export const CES_LEVELS = CASH_OUT_AFFILIATE_LEVELS.map((row) => ({
  level: row.level,
  ultraRequired: row.ultraRequired,
  directReferrals: row.directReferrals,
  cesPercentage: row.cashOutPercent,
  royaltyPercentage: 0,
}));

export const CASH_OUT_AFFILIATE_LEVEL_1_PERCENT =
  CASH_OUT_AFFILIATE_LEVELS[0].cashOutPercent;

export function isUltraMemberReferralNode(referral) {
  return referral?.referredUserData?.hasPurchasedPackage === true;
}

/** Ultra count in one referral node and its subtree (one level-1 leg). */
export function countUltraInReferralSubtree(referral) {
  if (!referral) return 0;
  let count = isUltraMemberReferralNode(referral) ? 1 : 0;
  const subs = referral.subReferrals || [];
  for (const sub of subs) {
    count += countUltraInReferralSubtree(sub);
  }
  return count;
}

/** Per level-1 leg Ultra counts (same order as direct children in referral tree). */
export function extractLegUltraCountsFromReferralTree(referralTree = []) {
  if (!Array.isArray(referralTree)) return [];
  return referralTree.map((leg) => countUltraInReferralSubtree(leg));
}

export function countUltraMembersInTree(referralTree = []) {
  const legs = extractLegUltraCountsFromReferralTree(referralTree);
  return legs.reduce((sum, n) => sum + n, 0);
}

export function countDirectUltraReferrals(referralTree = []) {
  if (!Array.isArray(referralTree)) return 0;
  return referralTree.filter((r) => isUltraMemberReferralNode(r)).length;
}

export function maxUltraPerLegForTeamRequirement(minTeamRequired) {
  return Math.floor(
    minTeamRequired * CASH_OUT_AFFILIATE_TEAM_LEG_CAP_FRACTION
  );
}

/** Capped team score toward `minTeamRequired` (levels 2+). */
export function computeCappedUltraTeamScore(legUltraCounts, minTeamRequired) {
  if (!minTeamRequired || minTeamRequired <= 0) return 0;
  const maxPerLeg = maxUltraPerLegForTeamRequirement(minTeamRequired);
  const legs = legUltraCounts || [];
  return legs.reduce((sum, legCount) => sum + Math.min(maxPerLeg, legCount), 0);
}

export function calculateCesEligibilityFromCounts(
  ultraMembersCount,
  directReferralsCount,
  legUltraCounts = null
) {
  let currentLevel = 0;
  let cesPercentage = 0;
  let nextLevel = null;

  for (const level of CASH_OUT_AFFILIATE_LEVELS) {
    const teamCount =
      level.level <= 1
        ? ultraMembersCount
        : computeCappedUltraTeamScore(legUltraCounts, level.ultraRequired);

    const meetsUltraRequirement = teamCount >= level.ultraRequired;
    const meetsDirectReferralRequirement =
      directReferralsCount >= level.directReferrals;

    if (meetsUltraRequirement && meetsDirectReferralRequirement) {
      currentLevel = level.level;
      cesPercentage = level.cashOutPercent;
    } else if (!nextLevel) {
      nextLevel = {
        level: level.level,
        ultraRequired: level.ultraRequired,
        directReferrals: level.directReferrals,
        cesPercentage: level.cashOutPercent,
        teamCountUsed: teamCount,
        usesLegCap: level.level >= 2,
        maxPerLeg:
          level.level >= 2
            ? maxUltraPerLegForTeamRequirement(level.ultraRequired)
            : null,
        ultraShortfall: Math.max(0, level.ultraRequired - teamCount),
        directReferralShortfall: Math.max(
          0,
          level.directReferrals - directReferralsCount
        ),
      };
      break;
    }
  }

  return {
    currentLevel,
    cesPercentage,
    ultraMembersCount,
    directReferralsCount,
    legUltraCounts,
    nextLevel,
    isEligible: currentLevel > 0 && cesPercentage > 0,
  };
}

export function calculateCesEligibilityFromReferralTree(referralTree = []) {
  const legUltraCounts = extractLegUltraCountsFromReferralTree(referralTree);
  const ultraMembersCount = legUltraCounts.reduce((s, n) => s + n, 0);
  const directReferralsCount = countDirectUltraReferrals(referralTree);
  return calculateCesEligibilityFromCounts(
    ultraMembersCount,
    directReferralsCount,
    legUltraCounts
  );
}
